import * as turf from '@turf/turf';
import geographiclib from 'geographiclib-geodesic';
import {
  Feature,
  LineString,
  MultiLineString,
  GeoJsonProperties,
} from 'geojson';
import { Frame } from '@/types/location';

const DEFAULT_STITCH_MAX_DISTANCE = 20;
const DEFAULT_STITCH_MAX_LAG = 300;
const DEFAULT_STITCH_MAX_ANGLE = 30;

const WGS84 = geographiclib.Geodesic.WGS84;

export const absAngularDelta = (a: number, b: number): number => {
  const delta = Math.abs(a - b);
  return delta <= 180 ? delta : 360 - delta;
};

export const boundariesIntersect = (a: Frame[], b: Frame[]): boolean => {
  const a0 = new Date(a[0].timestamp);
  const a1 = new Date(a[a.length - 1].timestamp);
  const b0 = new Date(b[0].timestamp);
  const b1 = new Date(b[b.length - 1].timestamp);

  const latestStart = new Date(Math.max(a0.getTime(), b0.getTime()));
  const earliestEnd = new Date(Math.min(a1.getTime(), b1.getTime()));

  return latestStart < earliestEnd;
};

/**
 * Matches frames with invalid bearing values to the closest valid sibling in the array
 */
export const matchInvalidBearingToClosest = (frames: Frame[]): Frame[] => {
  const framesWithBearing: number[] = [];

  frames.forEach((frame, index) => {
    if (frame.bearing) {
      framesWithBearing.push(index);
    }
  });

  frames.forEach((frame, index) => {
    if (!frame.bearing) {
      let closestIndex: number | null = null;
      let minDistance: number = Infinity;

      for (const bearingIndex of framesWithBearing) {
        const distance = Math.abs(bearingIndex - index);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = bearingIndex;
        }
      }

      if (closestIndex !== null) {
        frame.bearing = frames[closestIndex].bearing;
      }
    }
  });

  return frames;
};

/**
 * Takes all sequences of frames for a location and:
 *   - Snaps it to the nearest line
 *   - Adds a bearing value to the frame based on new snapped position
 */
export const snapToLine = (
  sequences: Frame[][],
  segments: MultiLineString,
): Frame[][] => {
  const snapped = sequences.map(seq => {
    const newSeq = seq.map((frame, index) => {
      const prevFrame = seq[index - 1];
      const nextFrame = seq[index + 1];

      const pt = turf.point([frame.position.lon, frame.position.lat]);
      const snapped = turf.nearestPointOnLine(segments, pt);

      frame.position.lon = snapped.geometry.coordinates[0];
      frame.position.lat = snapped.geometry.coordinates[1];

      if (!prevFrame) {
        return frame;
      }

      prevFrame.bearing = turf.bearing(
        turf.point([prevFrame.position.lon, prevFrame.position.lat]),
        turf.point([frame.position.lon, frame.position.lat]),
      );

      if (!nextFrame) {
        frame.bearing = prevFrame.bearing;
      }

      return frame;
    });

    const backfilled = matchInvalidBearingToClosest(newSeq);
    return backfilled;
  });

  return snapped;
};

const normalizeHeading = (heading: number): number => {
  return heading < 0 ? heading + 360 : heading;
};

/**
 * Filters headings that are not going in the direction of the line segments (intersections)
 *   - Compares each frame's heading to the nearest line, if the heading is not within a 45 degree tolerance, the frame is discarded.
 */
export const filterInvalidHeadings = (
  sequences: Frame[][],
  segments: MultiLineString,
): Frame[][] => {
  const filteredSequences = sequences
    .map(seq => {
      return seq.filter((frame, index) => {
        const currentFrame = frame;
        const nextFrame = seq[index + 1];

        if (!nextFrame) {
          return true;
        }

        let lineHeading;
        const frameHeading = turf.bearing(
          turf.point([currentFrame.position.lon, currentFrame.position.lat]),
          turf.point([nextFrame.position.lon, nextFrame.position.lat]),
        );

        const pt = turf.point([frame.position.lon, frame.position.lat]);
        const nearestPoint = turf.nearestPointOnLine(segments, pt);

        let minDistance = Infinity;
        let closest: Feature<LineString> | null = null;

        const lines = segments.coordinates.flatMap(lineCoords => {
          const lineSegments = [];
          for (let i = 0; i < lineCoords.length - 1; i++) {
            const segment = {
              type: 'LineString',
              coordinates: [lineCoords[i], lineCoords[i + 1]],
            };
            lineSegments.push(segment);
          }
          return lineSegments;
        });

        lines.forEach(lineCoords => {
          const segment = turf.lineString(lineCoords.coordinates);
          const pointOnSegment = turf.nearestPointOnLine(segment, pt);
          const distance = turf.distance(nearestPoint, pointOnSegment);

          if (distance < minDistance) {
            minDistance = distance;
            closest = segment;
          }
        });

        if (closest) {
          const { geometry } = closest as Feature<
            LineString,
            GeoJsonProperties
          >;

          lineHeading = turf.bearing(
            turf.point(geometry.coordinates[0]),
            turf.point(geometry.coordinates[1]),
          );

          const delta = Math.abs(
            normalizeHeading(frameHeading) - normalizeHeading(lineHeading),
          );

          const tolerance = 45;
          const normalizedDelta = Math.min(delta, 360 - delta);
          const isParallel = normalizedDelta <= tolerance;
          const isAntiParallel =
            180 - tolerance <= normalizedDelta &&
            normalizedDelta <= 180 + tolerance;

          return isParallel || isAntiParallel;
        } else {
          return false;
        }
      });
    })
    .filter(seq => seq.length > 1);

  return filteredSequences;
};

export const stitch = (
  frames: Frame[],
  maxDistance = DEFAULT_STITCH_MAX_DISTANCE,
  maxLag = DEFAULT_STITCH_MAX_LAG,
  maxAzimuthDelta = DEFAULT_STITCH_MAX_ANGLE,
) => {
  if (frames.length === 0) return [[]];

  const sequences: Record<string, Frame[]> = {};

  frames.forEach(frame => {
    const sequence = frame.sequence;
    if (!sequences[sequence]) sequences[sequence] = [];
    sequences[sequence].push(frame);
  });

  const skipStitching: Frame[][] = [];
  const seqs = Object.values(sequences)
    .filter(seq => {
      if (seq.length > 1) {
        return true;
      } else {
        skipStitching.push(seq);
        return false;
      }
    })
    .map(seq => seq.sort((a, b) => a.idx - b.idx))
    .sort(
      (a, b) =>
        new Date(a[0].timestamp).getTime() - new Date(b[0].timestamp).getTime(),
    );

  const firstSeq = seqs.shift() || [];
  const collections: Frame[][][] = [[firstSeq]];

  let currentCollection: Frame[][] = collections[collections.length - 1] || [];
  let remaining: Frame[][] = [];

  while (seqs.length > 0) {
    const seq = seqs.shift() || [];

    if (
      boundariesIntersect(currentCollection[currentCollection.length - 1], seq)
    ) {
      remaining.push(seq);

      if (seqs.length === 0) {
        const firstRemaining = remaining.shift() || [];
        collections.push([firstRemaining]);
        currentCollection = collections[collections.length - 1] || [];
        seqs.push(...remaining);
        remaining = [];
      }

      continue;
    }

    const last2DElement = currentCollection[currentCollection.length - 1] || [];
    const last3DElement = last2DElement[last2DElement.length - 1];
    const secondToLast3DElement = last2DElement[last2DElement.length - 2];

    const t0 = new Date(last3DElement.timestamp).getTime();
    const t1 = new Date(seq[0].timestamp).getTime();

    if ((t1 - t0) / 1000 > maxLag) {
      // ☝️ Convert milliseconds to seconds
      remaining.push(seq);

      if (seqs.length === 0) {
        const firstRemaining = remaining.shift() || [];
        collections.push([firstRemaining]);
        currentCollection = collections[collections.length - 1];
        seqs.push(...remaining);
        remaining = [];
      }

      continue;
    }

    const { lat: lat_a0, lon: lon_a0 } = secondToLast3DElement.position;
    const { lat: lat_a1, lon: lon_a1 } = last3DElement.position;
    const { lat: lat_b0, lon: lon_b0 } = seq[0].position;
    const { lat: lat_b1, lon: lon_b1 } = seq[1].position;

    const from = turf.point([lon_a1, lat_a1]);
    const to = turf.point([lon_b0, lat_b0]);
    const meters = turf.distance(from, to, { units: 'meters' });

    if (meters > maxDistance) {
      remaining.push(seq);

      if (seqs.length === 0) {
        const firstRemaining = remaining.shift() || [];
        collections.push([firstRemaining]);
        currentCollection = collections[collections.length - 1];
        seqs.push(...remaining);
        remaining = [];
      }

      continue;
    }

    const azi_a = WGS84.Inverse(lat_a0, lon_a0, lat_a1, lon_a1).azi2;
    const azi_b = WGS84.Inverse(lat_b0, lon_b0, lat_b1, lon_b1).azi2;
    const delta_azi = absAngularDelta(azi_a!, azi_b!);

    if (delta_azi > maxAzimuthDelta) {
      remaining.push(seq);

      if (seqs.length === 0) {
        const firstRemaining = remaining.shift() || [];
        collections.push([firstRemaining]);
        currentCollection = collections[collections.length - 1];
        seqs.push(...remaining);
        remaining = [];
      }

      continue;
    }

    currentCollection.push(seq);

    if (seqs.length === 0 && remaining.length > 0) {
      const firstRemaining = remaining.shift() || [];
      collections.push([firstRemaining]);
      currentCollection = collections[collections.length - 1];
      seqs.push(...remaining);
      remaining = [];
    }
  }

  const stitched = collections.map(coll => coll.flat());
  const skipped = skipStitching.map(coll => coll.flat());

  return [...stitched, ...skipped];
};
